import { useContext, useState } from 'react';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Button, Flex, Text } from '@raystack/apsara';
import clsx from 'clsx';
import { mutate } from 'swr';

import { apiClient } from '@src/clients';
import { IdentityContext } from '@src/contexts/identity/context';
import { Show } from '@src/hoc';
import { useUserHasPermissions } from '@src/hooks';
import { onPopupClose, showPopup } from '@src/utils/popups';

import { KycPopup } from './KycPopup';

import styles from './WithAddToCartImageThumbnail.module.css';

interface WithAddToCartImageThumbnail {
  is_accessible: boolean;
  children: React.ReactNode;
  classes: { visible: string };
  price: number;
  isInCart: boolean;
  orderId: string;
  isCartLoading: boolean;
  setOpen: (open: boolean) => void;
}

export const WithAddToCartImageThumbnail = ({
  is_accessible,
  classes,
  setOpen,
  children,
  price,
  isInCart,
  orderId,
  isCartLoading
}: WithAddToCartImageThumbnail) => {
  const [isLoading, setIsLoading] = useState(false);

  const { activeOrgInfo, activeOrg } = useContext(IdentityContext);
  const isKycSuccessful = activeOrgInfo?.kyc_successful === true;

  const { permissions } = useUserHasPermissions([
    { permission: 'delete', resource: `app/organization:${activeOrg?.id}` }
  ]);

  const isOrgOwner = permissions[0]?.status ?? false;

  if (is_accessible) return children;

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();

    if (isKycSuccessful) {
      setOpen(true);
    } else {
      const popup: ReturnType<typeof showPopup> = showPopup(KycPopup, {
        onClose: () => onPopupClose(popup),
        isOrgOwner
      });
    }
  }

  async function handleRemove(e: React.MouseEvent) {
    e.stopPropagation();

    setIsLoading(true);

    try {
      if (orderId) {
        await apiClient.clerkApi.v1OrderItemsDelete({ id: orderId });
        await mutate('in-cart');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className={styles.withAddToCartImageThumbnailOverlayContainer}>
        <div className={styles.withAddToCartImageThumbnailOverlay}>
          <LockClosedIcon
            className={styles.withAddToCartImageThumbnailOverlayIcon}
          />

          <Flex
            className={clsx(
              styles.withAddToCartImageThumbnailOverlayContent,
              classes.visible
            )}
            justify='between'
            align='center'
          >
            <Show when={!!price}>
              <Text size={4} weight={500} className={styles.price}>
                ${price}
              </Text>
            </Show>

            <Show
              when={!isInCart}
              fallback={
                <Button
                  size='small'
                  variant='solid'
                  color='danger'
                  loading={isLoading || isCartLoading}
                  disabled={isLoading || isCartLoading}
                  onClick={handleRemove}
                  data-test-id='remove-from-cart'
                >
                  Remove
                </Button>
              }
            >
              <Button
                size='small'
                loading={isCartLoading}
                disabled={isCartLoading}
                onClick={handleClick}
                data-test-id='add-to-cart'
              >
                Add to cart
              </Button>
            </Show>
          </Flex>
        </div>

        {children}
      </div>
    </>
  );
};
